// InventoryList.js

import React, { useState } from 'react';
import { formatCurrency } from '../Help/helper';
import { useNavigate } from 'react-router-dom';
import { Button, Badge, Input, EmptyState } from '../UI';
import { MagnifyingGlassIcon, PencilSquareIcon, ArrowsUpDownIcon } from '@heroicons/react/20/solid';

const InventoryList = ({
  inventoryItems,
  openAllocationModal,
  handleEdit,
  handleDelete,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const navigate = useNavigate();

  // Function to get field value for sorting
  const getFieldValue = (item, field) => {
    if (field === 'name') {
      return item.type === 'part' ? item.partName : item.description;
    } else {
      return item[field];
    }
  };

  // Filtered inventory items based on search term
  const filteredInventoryItems = inventoryItems.filter((item) => {
    const itemName = item.type === 'part' ? item.partName : item.description;
    const matchesSearch = itemName.toLowerCase().includes(searchTerm.toLowerCase());
    const hasPositiveQuantity = item.quantity > 0;
    return matchesSearch && hasPositiveQuantity;
  });
  

  // Sorted inventory items based on sort field and direction
  const sortedInventoryItems = [...filteredInventoryItems].sort((a, b) => {
    let fieldA = getFieldValue(a, sortField);
    let fieldB = getFieldValue(b, sortField);

    // Handle null or undefined values
    if (fieldA == null) fieldA = '';
    if (fieldB == null) fieldB = '';

    // If both fields are strings, compare as strings
    if (typeof fieldA === 'string' && typeof fieldB === 'string') {
      fieldA = fieldA.toLowerCase();
      fieldB = fieldB.toLowerCase();
      if (fieldA < fieldB) return sortDirection === 'asc' ? -1 : 1;
      if (fieldA > fieldB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    }

    // If both fields are numbers, compare as numbers
    if (typeof fieldA === 'number' && typeof fieldB === 'number') {
      if (fieldA < fieldB) return sortDirection === 'asc' ? -1 : 1;
      if (fieldA > fieldB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    }

    // If types are different, convert both to strings and compare
    fieldA = fieldA.toString().toLowerCase();
    fieldB = fieldB.toString().toLowerCase();
    if (fieldA < fieldB) return sortDirection === 'asc' ? -1 : 1;
    if (fieldA > fieldB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleViewDetails = (itemId) => {
    navigate(`/inventory/${itemId}`);
  };

  const getStockBadge = (quantity) => {
    if (quantity === 0) return <Badge variant="danger">Out of Stock</Badge>;
    if (quantity <= 5) return <Badge variant="warning">Low Stock</Badge>;
    return <Badge variant="success">In Stock</Badge>;
  };

  return (
    <>
      {/* Search and Sort Controls */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between mb-4">
        <div className="flex-1 w-full sm:max-w-md">
          <Input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search items..."
            icon={<MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />}
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 font-medium">Sort by:</span>
          <select
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
          >
            <option value="name">Name</option>
            <option value="quantity">Quantity</option>
            <option value="costPrice">Cost Price</option>
          </select>
          <Button
            onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
            variant="secondary"
            size="sm"
          >
            <ArrowsUpDownIcon className="h-4 w-4 mr-1" />
            {sortDirection === 'asc' ? 'Asc' : 'Desc'}
          </Button>
        </div>
      </div>

      {/* Inventory Items Table */}
      <div className="overflow-x-auto">
        {sortedInventoryItems.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Item Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Cost Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Quantity
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Stock Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Supplier
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedInventoryItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleViewDetails(item.id)}
                      className="text-sm font-medium text-primary-600 hover:text-primary-900 transition-colors"
                    >
                      {item.type === 'part' ? item.partName : item.description}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-gray-900">
                      {formatCurrency(item.costPrice)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 font-medium">{item.quantity}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStockBadge(item.quantity)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">{item.supplier || 'N/A'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        onClick={() => handleEdit(item)}
                        variant="secondary"
                        size="sm"
                      >
                        <PencilSquareIcon className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => openAllocationModal(item.id)}
                        variant="success"
                        size="sm"
                      >
                        Allocate
                      </Button>
                      <Button
                        onClick={() => handleDelete(item.id)}
                        variant="danger"
                        size="sm"
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState
            title="No inventory items found"
            message="Try adjusting your search criteria or add new items to inventory."
            icon={
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            }
          />
        )}
      </div>
    </>
  );
};

export default InventoryList;
